import { Box, Center, Flex, Heading } from '@chakra-ui/layout';
import { Spinner } from '@chakra-ui/react';
import React, { useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProfileContext, {
    getProfileErrorText,
    getProfileErrorHeader,
} from '../context/ProfileContext';
import { useToast } from '@chakra-ui/react';
import { Timeout } from '../util/types';

export const Home: React.FC<{}> = () => {
    const profile = useContext(ProfileContext);
    const areLicensesLoaded = useRef(false);
    const toast = useToast();
    const timeoutRef = useRef<Timeout>();

    const licenseChecker = () => {
        if (!areLicensesLoaded.current) {
            console.log('Experiencing a slow response time...');
            return toast({
                title: 'Still Loading',
                description: 'Hmm, this is taking longer than expected...',
                status: 'warning',
                duration: 9000,
                variant: 'subtle',
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        areLicensesLoaded.current = profile?.licensesLoaded;
    }, [profile]);

    useEffect(() => {
        timeoutRef.current = setTimeout(licenseChecker, 9000);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const renderGames = () => (
        <>
            <Heading textAlign="center" as="h3" size="lg">
                My Games
            </Heading>
            {profile.licensesLoaded && Object.values(profile.licenses).length === 0 && (
                <Center>
                    <Heading size="small" as="h5">
                        You have no games yet. Add your first!
                    </Heading>
                </Center>
            )}
            <Flex wrap="wrap" justifyContent="center">
                {Object.values(profile.licenses).map((license, i) => (
                    <Link key={`license_${i}`} to={`/game/${license.key}/overview`}>
                        <Box
                            width="200px"
                            height="200px"
                            margin={5}
                            padding={5}
                            border="1px solid black"
                            borderRadius="0.5em"
                        >
                            <Center height="100%">
                                <Heading as="h4" size="md" data-cy="game-name">
                                    {license.configs?.name || 'Unnamed Game'}
                                </Heading>
                            </Center>
                        </Box>
                    </Link>
                ))}
                <Link to="/create">
                    <Box
                        width="200px"
                        height="200px"
                        margin={5}
                        padding={5}
                        border="1px solid black"
                        borderRadius="0.5em"
                    >
                        <Center height="100%">
                            <Heading as="h4" size="md">
                                +
                            </Heading>
                        </Center>
                    </Box>
                </Link>
            </Flex>
        </>
    );

    const renderLoading = () => (
        <Center width="100%" height="100%">
            <Spinner data-cy="loading" />
        </Center>
    );

    const renderError = () => (
        <Center width="100%" height="100%" display="flex" flexDirection="column">
            <Heading as="h3" size="md">
                {profile.errors.map((error) => getProfileErrorHeader(error))}
            </Heading>
            {profile.errors.map((error) => getProfileErrorText(error))}
        </Center>
    );

    return (
        <Center width="100%" data-cy="my-games">
            <Box width="100%" padding={5}>
                {profile.errors.length > 0 ? (
                    <>
                        <Heading textAlign={'center'} marginBottom={'1em'}>
                            Oh No! Error Detected
                        </Heading>
                        {renderError()}
                    </>
                ) : (
                    <>{profile.licensesLoaded ? renderGames() : renderLoading()}</>
                )}
            </Box>
        </Center>
    );
};
